import { Action } from "models/Actions/Action";
import { EvalReviewActionMetadata } from "models/Actions/Evaluation/EvalReviewActionMetadata";
import { Answer } from "models/Answer";
import { ClassifierResultValidation } from "models/ClassifierResultValidation";
import { LicensedDataProcessing } from "models/DataProcesses";
import Evaluation, { EvalType, EvaluationStatus } from "models/Evaluation";
import EvaluationModule from "models/EvaluationModule";
import { HighlightsForTagResults } from "models/HighlightsForTagResults";
import Interaction from "models/Interaction";
import { InteractionType } from "models/InteractionType";
import { ApplicationUser } from "models/Permission/ApplicationUser";
import SoundClip from "models/SoundClip";
import { WorkflowInstance } from "models/Workflows/WorkflowInstance";
import { Moment } from "moment";
import { WorkflowConditionType } from "../components/Admin/Organizations/OrganizationDetail/OrganizationWorkflows/Types/WorkflowModels";
import { WorkflowMessagingMetadata } from "../models/Workflows/WorkflowMessagingMetadata";
import BaseService from "./BaseService";

interface EvaluationServiceHierarchy {
    evaluation: Evaluation;
    serviceHierarchy?: string[];
    agentName: string;
}

export class EvaluationUpdateResponse {
    evaluation: Evaluation;
    workflowResponse: {
        approveSignal: string;
        rejectSignal: string;
        workflowInstanceId: string;
    };
    moduleScoresMap: Map<string, number>;
    moduleScores: string;
}

export interface WorkflowMetadata extends Partial<WorkflowMessagingMetadata> {
    level: number;
    id: string;
    licensedModuleId: string;
    type: WorkflowConditionType;
    name: string;
}

export interface EvaluationAnswerHistory {
    answerHistoryObjectsList: AnswerHistoryObject[];
    moduleId: string;
}

interface AnswerHistoryObject {
    answerHistoriesList: AnswerHistoryItem[];
    questionId: string;
    questionText: string;
}

interface AnswerHistoryItem {
    previousValues: string[];
    currentValues: string[];
    changedBy: string;
    changedOn: string;
}

export class EvaluationRequestModel {
    evaluation: Evaluation;
    workflowMetadata: Record<string, WorkflowMetadata[]>;
    permissions?: IEvalPermissionsModel;
    errorCode?: string;
    isRedacted?: boolean;
}

export interface IEvalPermissionsModel {
    canChangeDisputedAnswers: boolean;
    canConvertToAnalystEvaluation: boolean;
    canDispute: boolean;
    canEdit: boolean;
}

export interface EvaluationForList {
    evaluation: {
        id: string;
        evaluationQbId?: number;
        evaluationStatus: EvaluationStatus;
        modifiedOn?: string;
        submittedDate?: string;
        interaction: {
            interactionDate?: Date | Moment | string;
            interactionType?: InteractionType;
            audioMetadata: {
                blobFileKey?: string;
                callDurationMillis?: number;
            };
        };
    };
    agentName: string;
    serviceHierarchy?: string[];
}

export interface EvaluationData {
    errorCode?: string; // logs
    evaluation: {
        actionThread?: Action<EvalReviewActionMetadata>; // EvalStore >> EvalNav
        agentAcknowledged?: boolean; // EvalNav
        agentCoachingId?: string; //EvalCoachingTableViewModel
        analyst?: ApplicationUser;
        analystId?: string; // EvalPage
        analystTimeSpent?: string; // Eval Page
        answers: Answer[]; // EvalStore
        //assignedDate?: string; // Not used
        canChangeDisputedAnswers?: boolean; // ModuleDisputedMessage
        classifierResultValidations: ClassifierResultValidation[]; // ClassifierFeedbackAnswer
        //conversionType?: number; // Not used
        //convertedBy?: string; // Not used
        createdBy?: string; // Part of BaseEntity
        createdOn?: string; // FocusedAdditionalMetadata - Part of BaseEntity
        deletedOn?: string; // Part of BaseEntity
        evaluationModules: EvaluationModule[]; // EvalStore
        evaluationQbId?: number; // AgentChatStore
        evaluationStatus: EvaluationStatus; // EvalStore
        id: string; // Unique Id
        interaction?: Interaction; // EvalStore
        interactionId?: string; // Not used in evals
        isActive?: boolean; // Unused - Part of BaseEntity
        isDisputable?: boolean; // EvalStore >> EvalNav
        isEditable?: boolean; // ClassifierResultFeedbackCard
        modifiedBy?: string; // Part of BaseEntity
        modifiedOn?: string; // EvalStore -> prepareEvalForSerialization - Part of BaseEntity
        organization: {
            // simplified organization object
            licensedData: LicensedDataProcessing[]; // EvalStore
        };
        organizationId: string; // EvalStore
        previouslyDisputed?: boolean; // EvalStore >> EvalNav
        soundClips?: SoundClip[]; // EvalStore
        submittedDate?: string; // EvalCoachingTableViewModel
        type: EvalType; // EvalStore >> ConversationsStore
        workflowInstances?: WorkflowInstance[]; // EvalWorkflowStore
        // Not in request but required for the Evaluation Class
        qbAppId?: string; // EvalStore
        allActionThreads?: Action<EvalReviewActionMetadata>[]; // EvalStore >> EvalNav
    };
    isRedacted?: boolean; // EvalPage
    permissions?: {
        canChangeDisputedAnswers: boolean; // Used in QuestionAnswerList
        canConvertToAnalystEvaluation: boolean; // Used in EvalNav
        canDispute: boolean; // Not needed for evals
        canEdit: boolean; // Not needed for evals
    };
    workflowMetadata: Record<string, WorkflowMetadata[]>; // EvalWorkflowStore
}

export class EvaluationService extends BaseService {
    protected urlBase: string = "api/Evaluations";

    public async getEvalGraph(organizationId: string, evaluationId: string) {
        const res = await this.post<EvaluationRequestModel>(
            JSON.stringify({ organizationId, evaluationId }),
            new URLSearchParams({}),
            "GetEval",
        );

        const evaluationData: EvaluationData = {
            evaluation: {
                qbAppId: res.evaluation.qbAppId,
                agentAcknowledged: res.evaluation.agentAcknowledged,
                evaluationQbId: res.evaluation.evaluationQbId,
                organizationId: organizationId,
                organization: {
                    licensedData: res.evaluation.organization?.licensedData,
                },
                submittedDate: res.evaluation.submittedDate,
                interaction: res.evaluation.interaction,
                interactionId: res.evaluation.interactionId,
                soundClips: res.evaluation.soundClips,
                evaluationModules: res.evaluation.evaluationModules,
                answers: res.evaluation.answers,
                analystId: res.evaluation.analyst?.id,
                analyst: res.evaluation.analyst,
                evaluationStatus: res.evaluation.evaluationStatus,
                agentCoachingId: res.evaluation.agentCoachingId,
                classifierResultValidations:
                    res.evaluation.classifierResultValidations,
                workflowInstances: res.evaluation.workflowInstances,
                canChangeDisputedAnswers:
                    res.permissions?.canChangeDisputedAnswers,
                isEditable: res.permissions?.canEdit,
                isDisputable: res.permissions?.canDispute,
                type: res.evaluation.type,
                id: res.evaluation.id,
                createdOn: res.evaluation.createdOn,
                modifiedOn: res.evaluation.modifiedOn,
                deletedOn: res.evaluation.deletedOn,
                createdBy: res.evaluation.createdBy,
                modifiedBy: res.evaluation.modifiedBy,
            },
            workflowMetadata: res.workflowMetadata,
            errorCode: res.errorCode,
            isRedacted: res.isRedacted,
            permissions: res.permissions
                ? {
                      canChangeDisputedAnswers:
                          res.permissions.canChangeDisputedAnswers,
                      canConvertToAnalystEvaluation:
                          res.permissions.canConvertToAnalystEvaluation,
                      canDispute: res.permissions.canDispute,
                      canEdit: res.permissions.canEdit,
                  }
                : undefined,
        };

        return evaluationData;
    }

    public async getHighlightsForTag(tagId: string, amdId: string) {
        const res = await this.get<HighlightsForTagResults>(
            new URLSearchParams({
                tagId,
                amdId,
            }),
            "GetHighlightsForTag",
        );

        return res;
    }

    public async startNextEval(analystId: string, hierarchyId: string) {
        const res = await this.get<{ id: string | null }>(
            new URLSearchParams({
                analystId: analystId,
                hierarchyId: hierarchyId,
            }),
            "Start",
        );

        return res;
    }

    public async generateLiveEval(analystId: string, orgMemberId: string) {
        const res = await this.get<{ id: string | null }>(
            new URLSearchParams({
                analystId: analystId,
                orgStructureMemberId: orgMemberId,
            }),
            "StartLiveEval",
        );

        return res;
    }

    public async getEvalByEvaluationNumber(evalNumber: number, orgId: string) {
        const resp = await this.get<string>(
            new URLSearchParams({
                evaluationNumber: evalNumber.toString(),
                orgId: orgId,
            }),
            "GetEvalByEvalNumber",
        );

        return resp;
    }

    public async getEvalsForAnalyst(
        analystId: string,
        startDate: string,
        endDate: string,
        onlyComplete: boolean,
        debounceKey?: string,
    ) {
        const res = await this.get<EvaluationServiceHierarchy[]>(
            new URLSearchParams({
                analystId: analystId,
                startDate: startDate,
                endDate: endDate,
                onlyComplete: JSON.stringify(onlyComplete),
            }),
            "AnalystEvaluations",
            undefined,
            debounceKey,
        );

        const simplified: EvaluationForList[] = res.map((evalObj) => {
            const { evaluation, serviceHierarchy, agentName } = evalObj;
            return {
                evaluation: {
                    id: evaluation.id,
                    evaluationQbId: evaluation.evaluationQbId,
                    evaluationStatus: evaluation.evaluationStatus,
                    modifiedOn: evaluation.modifiedOn,
                    submittedDate: evaluation.submittedDate,
                    interaction: {
                        interactionDate:
                            evaluation.interaction?.interactionDate,
                        interactionType:
                            evaluation.interaction?.interactionType,
                        audioMetadata: {
                            blobFileKey:
                                evaluation.interaction?.audioMetadata
                                    ?.blobFileKey,
                            callDurationMillis:
                                evaluation.interaction?.audioMetadata
                                    ?.callDurationMillis,
                        },
                    },
                },
                agentName: agentName,
                serviceHierarchy: serviceHierarchy,
            };
        });

        return simplified;
    }

    public async updateEvalGraph(evaluation: EvaluationUpdateResponse) {
        const body = JSON.stringify(evaluation);
        const urlSearchParams: URLSearchParams = new URLSearchParams();
        const res = await this.put(urlSearchParams, "UpdateEval", body);
        return res;
    }

    public async updateEvalHierarchy(
        evaluationId: string,
        hierarchyId: string,
    ) {
        const urlSearchParams: URLSearchParams = new URLSearchParams({
            evaluationId,
            hierarchyId,
        });
        const res = await this.put(urlSearchParams, "UpdateEvalHierarchy");
        return res;
    }

    public async getAnswerHistoryForEval(evalId: string) {
        const res = await this.get<EvaluationAnswerHistory[]>(
            new URLSearchParams({
                evalId,
            }),
            "GetAnswerHistoryForEval",
        );

        return res;
    }

    public async convertToAnalystEvaluation(
        evaluationId: string,
        orgId: string,
        analystId: string,
        isMinimal: boolean,
    ) {
        await this.patch<EvaluationRequestModel>(
            null,
            new URLSearchParams({
                evaluationId,
                orgId,
                analystId,
                isMinimal: isMinimal.toString(),
            }),
            "ConvertToAnalystEvaluation",
        );
    }

    public async acknowledgeEvaluation(evalId: string) {
        await this.get(
            new URLSearchParams({
                evalId,
            }),
            "Acknowledge",
        );
    }

    public async addAnalystTimeSpent(
        evaluationId: string,
        timeInSeconds: number,
    ) {
        return this.patch(
            null,
            new URLSearchParams({
                evaluationId,
                timeInSeconds: timeInSeconds.toString(),
            }),
            "AddAnalystTimeSpent",
            undefined,
            undefined,
            undefined,
            {
                // ensures request isnt cancelled when tab is closed
                keepalive: true,
            },
            true, // asRawResponse
        );
    }
}
